import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, first, mergeMap, switchMap } from 'rxjs/operators';
import { PublicTokensState } from '../store/auth/auth.reducer';
import { AuthFacade } from './../store/auth/auth.facade';
import { AuthSessionService } from 'src/app/goiania-sso/services/auth-session.service';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authFacade: AuthFacade,
    private authSessionService: AuthSessionService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (req.method === 'OPTIONS') {
      return next.handle(req);
    }

    if (req.headers.has('no-token')) {
      const headers = req.headers.delete('no-token');
      return next.handle(req.clone({ headers }));
    }

    if (this.isGoianiaSsoExchangeEndpoint(req.url)) {
      let headers = req.headers;
      for (const h of ['x-auth-token', 'x-tn-token', 'X-AUTH-TOKEN', 'X-TN-TOKEN']) {
        if (headers.has(h)) headers = headers.delete(h);
      }
      return headers === req.headers
        ? next.handle(req)
        : next.handle(req.clone({ headers }));
    }

    const session = this.authSessionService.getSession();
    if (
      session &&
      this.isAbsoluteHttpUrl(req.url) &&
      !this.isGoianiaSsoExchangeEndpoint(req.url)
    ) {
      let headers = req.headers;

      const tokens: Array<{ value?: string; headerNames: string[] }> = [
        {
          value: session.xAuthToken,
          headerNames: ['x-auth-token', 'X-AUTH-TOKEN'],
        },
        {
          value: session.xTnToken,
          headerNames: ['x-tn-token', 'X-TN-TOKEN'],
        },
      ];

      for (const token of tokens) {
        if (!token.value) continue;
        for (const name of token.headerNames) {
          headers = this.setHeaderIfMissing(headers, name, token.value);
        }
      }

      if (headers !== req.headers) {
        return next.handle(req.clone({ headers }));
      }
    }

    if (req.headers.has('has-token')) {
      const headers = req.headers.delete('has-token');

      return this.authFacade.user$.pipe(
        filter((u) => !!u),
        first(),
        mergeMap((user) => {
          let newHeaders = headers;
          if (!newHeaders.has('X-AUTH-TOKEN')) {
            newHeaders = newHeaders.set('X-AUTH-TOKEN', user!.authToken);
          }
          if (!newHeaders.has('X-TN-TOKEN')) {
            newHeaders = newHeaders.set('X-TN-TOKEN', user!.tnToken);
          }

          return next.handle(
            req.clone({
              headers: newHeaders,
            })
          );
        })
      );
    }

    return this.authFacade.user$.pipe(
      first(),
      switchMap((user) => {
        if (user) {
          return of({
            authToken: user.authToken,
            tnToken: user.tnToken,
          });
        }
        return this.authFacade.publicTokens$.pipe(
          filter((pt): pt is PublicTokensState => !!pt),
          first()
        );
      }),
      mergeMap(({ authToken, tnToken }: PublicTokensState) => {
        if (
          req.url.includes('/icad') &&
          !req.url.includes('/perm') &&
          !req.url.includes('/django') &&
          !req.url.includes('consulta-geral')
        ) {
          const headers = req.headers.set(
            'Authorization',
            `Token ${authToken}`
          );

          return next.handle(
            req.clone({
              headers,
            })
          );
        }

        let headers = req.headers;
        if (!headers.has('X-AUTH-TOKEN')) {
          headers = headers.set('X-AUTH-TOKEN', authToken);
        }
        if (!headers.has('X-TN-TOKEN')) {
          headers = headers.set('X-TN-TOKEN', tnToken);
        }

        return next.handle(
          req.clone({
            headers,
          })
        );
      })
    );
  }

  private isAbsoluteHttpUrl(url: string): boolean {
    return typeof url === 'string' && /^https?:\/\//i.test(url);
  }

  private isGoianiaSsoExchangeEndpoint(url: string): boolean {
    return /\/auth\/goiania\/sso\/exchange\/?(\?.*)?$/i.test(url);
  }

  private setHeaderIfMissing(
    headers: HttpRequest<unknown>['headers'],
    name: string,
    value: string
  ) {
    return headers.has(name) ? headers : headers.set(name, value);
  }
}
